import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Award, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";

function googleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("trainee");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ name, email, password, role });
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="register-page">
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-orange-600 grid place-items-center rounded-sm">
              <Award className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black tracking-tight text-lg">
              PLATINUM<span className="text-orange-600">1</span>
            </span>
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Start the program</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-3 text-slate-950">
            Create your account.
          </h1>
          <p className="text-slate-600 mt-3">It's free to start. Pass all 8 quizzes to earn the badge.</p>

          <button
            type="button"
            onClick={googleSignIn}
            data-testid="google-signup-btn"
            className="mt-8 w-full border border-slate-300 hover:bg-slate-50 rounded-sm py-3 px-4 font-semibold text-slate-900 flex items-center justify-center gap-3 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.2 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.7 29.2 5 24 5 16.3 5 9.7 9.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.7 13.2-4.7l-6.1-5.1c-1.9 1.5-4.3 2.4-7.1 2.4-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.3 39 16 43.5 24 43.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6.001 0 6.1 5.1 6.1 5.1 4.4-4.1 6.7-10 6.7-16.2 0-1.2-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">or</div>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-900 mb-1 block">Full name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="w-full bg-white border border-slate-300 rounded-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-base p-3 outline-none"
                placeholder="Marcus Reynolds"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 mb-1 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="w-full bg-white border border-slate-300 rounded-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-base p-3 outline-none"
                placeholder="you@jobsite.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 mb-1 block">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="w-full bg-white border border-slate-300 rounded-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-base p-3 outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 mb-1 block">I'm registering as</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["trainee", "Trainee"],
                  ["instructor", "Instructor"],
                ].map(([v, l]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setRole(v)}
                    data-testid={`role-${v}-btn`}
                    className={`py-3 rounded-sm border text-sm font-semibold transition-colors ${
                      role === v
                        ? "bg-orange-600 border-orange-600 text-white"
                        : "bg-white border-slate-300 text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {error && <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              {loading ? "Creating..." : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-orange-700 font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-slate-950">
        <img src="https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-orange-400">Welcome to the Cohort</div>
          <div className="font-display text-3xl font-bold mt-3 max-w-md leading-tight">
            8 modules. 75% to pass. A credential homeowners trust.
          </div>
        </div>
      </div>
    </div>
  );
}
